/**
 * The mobile contact packet — the structured contact details a caller shared
 * through the Help Genie app, carried into the owner's inbox thread.
 *
 * Storage is the existing brain memory store (`account_memory`), NOT a new
 * table: one row per caller, `scope = 'caller'`, `caller_e164` = the caller's
 * number in E.164, `memory_key = 'contact_packet'`, and the structured fields
 * on `metadata`. The join from a conversation to a packet is `caller_e164`.
 *
 * The field set is grounded in what Help Genie Mobile can actually produce —
 * the `Profile` shape in `Help-Genie-Mobile/src/types/auth.ts`, which is what
 * HG-1299 ("collect user details progressively without a signup wall")
 * progressively fills in. Nothing here is invented beyond that shape.
 *
 * The producer (HG-1299) is not built yet, so in practice every read returns
 * an empty packet today. That is the expected state, and the UI says so
 * plainly rather than faking data or implying a load that never finishes.
 */

/** `account_memory.memory_key` a contact packet is stored under. */
export const MOBILE_CONTACT_PACKET_MEMORY_KEY = "contact_packet";

/** `account_memory.scope` a contact packet is stored under. */
export const MOBILE_CONTACT_PACKET_SCOPE = "caller";

/** Metadata schema version, so a later field change is detectable. */
export const MOBILE_CONTACT_PACKET_VERSION = 1;

export interface MobileContactPacketPhone {
  /** The caller's number in E.164, e.g. `+64211234567`. */
  e164: string | null;
  /** As the caller typed it, e.g. `021 123 4567`. Display only. */
  national: string | null;
  /** ISO 3166-1 alpha-2 country of the number, e.g. `NZ`. */
  countryCode: string | null;
}

export interface MobileContactPacketLocation {
  city: string | null;
  stateProvince: string | null;
  /** ISO 3166-1 alpha-2, e.g. `NZ`. */
  country: string | null;
  countryName: string | null;
}

export interface MobileContactPacketAddress {
  line1: string | null;
  line2: string | null;
  city: string | null;
  stateProvince: string | null;
  postalCode: string | null;
  /** ISO 3166-1 alpha-2, e.g. `NZ`. */
  country: string | null;
}

/** Where the packet came from. Unknown means the producer didn't say. */
export type MobileContactPacketSource = "mobile_app" | "unknown";

export interface MobileContactPacket {
  /** The caller this packet belongs to, normalised to E.164. */
  callerE164: string;
  name: string | null;
  email: string | null;
  phone: MobileContactPacketPhone | null;
  location: MobileContactPacketLocation | null;
  address: MobileContactPacketAddress | null;
  companyName: string | null;
  jobTitle: string | null;
  source: MobileContactPacketSource;
  /** When the caller last shared these details, ISO 8601. */
  collectedAt: string | null;
}

/** What a conversation's contact lookup resolved to. */
export interface ConversationContactPacket {
  /** The caller's number for this conversation, or null if there isn't one. */
  callerE164: string | null;
  /** The packet for that caller, or null when nothing has been shared yet. */
  packet: MobileContactPacket | null;
}

/**
 * The four honest states the contact block can be in. There is deliberately no
 * "unknown" state: a finished lookup with nothing in it is `empty`, not a
 * skeleton that implies it is still coming.
 */
export type ContactPacketStatus =
  /** The lookup is in flight. */
  | "loading"
  /** The lookup failed. */
  | "error"
  /** No caller number on this conversation, so nothing can be matched. */
  | "no-caller"
  /** Caller known, but they haven't shared any details. The common case today. */
  | "empty"
  /** A packet with at least one populated field. */
  | "present";
